import React, { ComponentType, useMemo } from 'react'
import { Spinner, Tab } from 'react-bootstrap'

import { InputParametersHierarchyInfo } from '../../views/Setup/AIFeaturePresets/PresetConfig/InputParametersHierarchyInfo'

import PrizeBadge from '../ScopedBlock/PrizeBadge.svg?url'
import { Modal } from '../Modal/Modal'
import { Tabs } from '../Tabs/Tabs'

import { DocumentTypeInputParametersHierarchyConfigComponentProps, InputParameterHierarchyType } from './model'

import { useInputParametersConfig } from './hooks'

export interface DocumentTypeInputParametersHierarchyConfigModalProps {
  show: boolean
  onHide: () => void
  type: InputParameterHierarchyType
  typeId: string
  name?: string
  documentTypeId: string
  inputParametersSchema: { [key: string]: any } | undefined
  defaultInputParameters: { [key: string]: any } | null
  DocumentTypeConfig: ComponentType<DocumentTypeInputParametersHierarchyConfigComponentProps>
  CurrentUserConfig: ComponentType<DocumentTypeInputParametersHierarchyConfigComponentProps>
  TeamMembersConfig?: ComponentType<DocumentTypeInputParametersHierarchyConfigComponentProps>
}

export const DocumentTypeInputParametersHierarchyConfigModal: React.FC<DocumentTypeInputParametersHierarchyConfigModalProps> = ({
  show,
  onHide,
  type,
  typeId,
  inputParametersSchema,
  defaultInputParameters,
  name,
  documentTypeId,
  DocumentTypeConfig,
  CurrentUserConfig,
  TeamMembersConfig,
}) => {
  const { ipHierarchyEnabled, hasIpHierarchyAdminScope, showTabs, teamMembersLoading } = useInputParametersConfig()

  const configProps = useMemo(() => {
    return {
      type,
      typeId,
      inputParametersSchema,
      defaultInputParameters,
      documentTypeId,
      onHide,
      showCustomizationSetting: true,
      ipHierarchyEnabled,
    }
  }, [type, typeId, inputParametersSchema, defaultInputParameters, documentTypeId, onHide, ipHierarchyEnabled])

  const userTitle = (
    <div className='d-flex align-items-center'>
      {!ipHierarchyEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Your Own
    </div>
  )

  const teamMembersTitle = (
    <div className='d-flex align-items-center'>
      {!ipHierarchyEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Your Team Members
    </div>
  )

  const tabConfig = (
    <Tabs defaultActiveKey={hasIpHierarchyAdminScope ? 'global' : 'currentUser'} id='preset-config-tabs' className='d-flex mb-3' mountOnEnter unmountOnExit>
      <Tab eventKey='global' title='Document Type Level'>
        <InputParametersHierarchyInfo
          icon='bi-exclamation-square'
          message='These settings apply to everyone, unless they have their own settings for the document type.'
        />
        <DocumentTypeConfig {...configProps} />
      </Tab>
      <Tab eventKey='currentUser' title={userTitle}>
        <InputParametersHierarchyInfo icon='bi-info-square' message='These settings will only apply to you for the document type. ' />
        <CurrentUserConfig {...configProps} />
      </Tab>
      {TeamMembersConfig && (
        <Tab eventKey='teamMembers' title={teamMembersTitle}>
          <InputParametersHierarchyInfo icon='bi-info-square' message='Manage settings for your team members for the document type. ' />
          <TeamMembersConfig {...configProps} />
        </Tab>
      )}
    </Tabs>
  )

  const singleConfig = ipHierarchyEnabled ? <CurrentUserConfig {...configProps} /> : <DocumentTypeConfig {...configProps} />

  const body = (
    <>
      <h4 className='text-center mb-5'>{`Customize ${name} for document type`}</h4>
      {teamMembersLoading ? (
        <div className='d-flex align-items-center justify-content-center p-2'>
          <Spinner size={'sm'} />
        </div>
      ) : showTabs ? (
        tabConfig
      ) : (
        singleConfig
      )}
    </>
  )

  return <Modal size='lg' show={show} onHide={onHide} header={<></>} body={body} footer={<></>} />
}
